import React from 'react';
import clsx from 'clsx';

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  hint?: string;
  error?: string;
  invalid?: boolean;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, hint, error, invalid, id, className, rows, ...props }, ref) => {
    const autoId = React.useId();
    const fieldId = id ?? autoId;
    const isInvalid = invalid || Boolean(error);
    return (
      <div className={clsx('flex flex-col gap-1.5', className)}>
        {label && (
          <label
            htmlFor={fieldId}
            className="text-[11px] font-medium tracking-wider uppercase text-slate-400"
          >
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={fieldId}
          rows={rows ?? 3}
          aria-invalid={isInvalid || undefined}
          aria-describedby={
            error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined
          }
          className={clsx(
            'w-full bg-surface-2 border rounded-ds-lg text-slate-50 px-3 py-2 text-[13px] outline-none transition-colors duration-150 resize-y',
            'placeholder:text-slate-500',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            isInvalid
              ? 'border-danger focus:border-danger'
              : 'border-white/[0.08] focus:border-accent',
          )}
          {...props}
        />
        {error ? (
          <p
            id={`${fieldId}-error`}
            className="text-[11px] text-danger-text"
          >
            {error}
          </p>
        ) : hint ? (
          <p
            id={`${fieldId}-hint`}
            className="text-[11px] text-slate-400"
          >
            {hint}
          </p>
        ) : null}
      </div>
    );
  },
);

Textarea.displayName = 'Textarea';

export default Textarea;
